import React, { useEffect, useState } from 'react'
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useAtom } from 'jotai'
import type { UsageDashboard } from '@shared/common-types.js'
import { themeAtom } from '../../atoms.js'
import { getChartTheme } from '../../components/charts/chartTheme.js'
import { bucketLabel, formatAxisTick, formatMetric } from './format.js'
import type { UsageMetric } from './format.js'

type OkDashboard = UsageDashboard & { state: 'ok' }

/** 窄屏（和 Tailwind 的 sm 断点一致）。Recharts 的高度、轴宽是 JS 属性，CSS 断点管不到 */
const NARROW_QUERY = '(max-width: 639px)'

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches)
  useEffect(() => {
    const media = window.matchMedia(NARROW_QUERY)
    const onChange = () => setNarrow(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return narrow
}

const CURRENT_COLOR = '#3b82f6'
const PREVIOUS_COLOR = '#9ca3af'

type Row = {
  label: string
  previousLabel: string
  current: number | null
  previous: number | null
}

type Props = {
  dashboard: OkDashboard
  metric: UsageMetric
  currentName: string
  previousName: string
  /** 图例那一行左边的小字，说明涨跌和谁比 */
  note: string
}

/**
 * 主图：当前时段实线 + 面积，对比时段（昨天 / 前一段）灰色虚线。
 * 今天还没到的小时没有实线，虚线照样画完，能看出今天比昨天同一时刻是高是低。
 */
export default function UsageChart({ dashboard, metric, currentName, previousName, note }: Props) {
  const [theme] = useAtom(themeAtom)
  const t = getChartTheme(theme)
  const narrow = useIsNarrow()
  const rows: Row[] = dashboard.series.map((point, i) => ({
    label: bucketLabel(dashboard.currentStart, dashboard.granularity, i),
    previousLabel: bucketLabel(dashboard.previousStart, dashboard.granularity, i),
    current: point.current ? point.current[metric] : null,
    previous: point.previous[metric],
  }))

  return (
    <div>
      <div className="flex items-center gap-3 px-1 text-xs text-gray-500 dark:text-gray-400 sm:gap-4 sm:px-2">
        <span className="mr-auto text-gray-400">{note}</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4" style={{ backgroundColor: CURRENT_COLOR }} />
          {currentName}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: PREVIOUS_COLOR }} />
          {previousName}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={narrow ? 190 : 300}>
        <ComposedChart data={rows} margin={{ top: 8, right: narrow ? 4 : 16, bottom: 0, left: -8 }}>
          <defs>
            <linearGradient id="usage-current-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CURRENT_COLOR} stopOpacity={0.18} />
              <stop offset="100%" stopColor={CURRENT_COLOR} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={t.gridColor} />
          <XAxis
            dataKey="label"
            tick={t.axisTickProps}
            axisLine={t.axisLineProps}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            tick={t.axisTickProps}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={narrow ? 44 : 48}
            tickFormatter={(value: number) => formatAxisTick(metric, value)}
          />
          <Tooltip
            cursor={{ stroke: t.cursorColor }}
            content={({ active, payload }) => {
              const row = payload?.[0]?.payload as Row | undefined
              if (!active || !row) return null
              return (
                <div style={t.tooltipContentStyle} className="px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-6">
                    <span style={{ color: CURRENT_COLOR }}>
                      {currentName} {row.label}
                    </span>
                    <span className="font-semibold" style={t.tooltipLabelStyle}>
                      {formatMetric(metric, row.current)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-6">
                    <span style={{ color: PREVIOUS_COLOR }}>
                      {previousName} {row.previousLabel}
                    </span>
                    <span style={t.tooltipItemStyle}>{formatMetric(metric, row.previous)}</span>
                  </div>
                </div>
              )
            }}
          />
          <Line
            type="linear"
            dataKey="previous"
            stroke={PREVIOUS_COLOR}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
          <Area
            type="linear"
            dataKey="current"
            stroke={CURRENT_COLOR}
            strokeWidth={2}
            fill="url(#usage-current-fill)"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
